import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ModalOverlay,
  ModalCard,
  ModalTitle,
  ModalDesc,
  ModalActions,
  ModalBtn,
  FormGrid,
  Field,
  Label,
  LabelRow,
  Counter,
  InputWrap,
  LeftIcon,
  Input,
  Hint,
} from "./styles";
import { userAPI } from "api";

type AddUserForm = {
  username: string;
  displayname: string;
  department: string;
  email: string;
  ouname: string; // ✅ 추가
};

const ModalPortal = ({ children }: { children?: any }) => {
  if (typeof document === "undefined") return null;
  return createPortal(children, document.body);
};

const AddUserModal = ({ isOpen, onClose }) => {
  const [values, setValues] = useState<AddUserForm>({
    username: "",
    displayname: "",
    department: "",
    email: "",
    ouname: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const firstInputRef = useRef<HTMLInputElement>(null);

  const usernameValid = /^[a-zA-Z0-9._-]{3,20}$/.test(values.username);
  const displaynameValid = values.displayname.trim().length >= 2;
  const departmentValid = values.department.trim().length >= 1;
  const emailValid =
    values.email.trim().length > 0 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email);
  const ounameValid = values.ouname.trim().length >= 1;

  const canSubmit = useMemo(
    () =>
      usernameValid &&
      displaynameValid &&
      departmentValid &&
      emailValid &&
      ounameValid &&
      !submitting,
    [
      usernameValid,
      displaynameValid,
      departmentValid,
      emailValid,
      ounameValid,
      submitting,
    ]
  );

  const update =
    (key: keyof AddUserForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setValues((v) => ({ ...v, [key]: e.target.value }));
    };

  const resetForm = () => {
    setValues({
      username: "",
      displayname: "",
      department: "",
      email: "",
      ouname: "",
    });
  };

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => firstInputRef.current?.focus(), 0);
    } else {
      resetForm();
      setSubmitting(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Enter" && canSubmit) handleSubmit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, canSubmit]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      await userAPI.postUser(
        values.username,
        values.displayname,
        values.department,
        values.email,
        values.ouname
      );

      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalPortal>
      <ModalOverlay
        role="dialog"
        aria-modal="true"
        aria-labelledby="adduser-title"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <ModalCard onClick={(e) => e.stopPropagation()}>
          <ModalTitle id="adduser-title">유저 생성</ModalTitle>
          <ModalDesc>새 유저 정보를 입력하세요.</ModalDesc>

          <FormGrid>
            {/* Username */}
            <Field>
              <LabelRow>
                <Label htmlFor="username">Username</Label>
                <Counter>{values.username.length}/20</Counter>
              </LabelRow>
              <InputWrap data-valid={usernameValid}>
                <LeftIcon viewBox="0 0 24 24" aria-hidden>
                  <path
                    d="M16 21v-2a4 4 0 00-4-4H7a4 4 0 00-4 4v2M9.5 11a4 4 0 100-8 4 4 0 000 8"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </LeftIcon>
                <Input
                  id="username"
                  ref={firstInputRef}
                  placeholder="예: gildong_hong"
                  value={values.username}
                  onChange={update("username")}
                  aria-invalid={!usernameValid}
                  required
                  aria-required="true"
                />
              </InputWrap>
              <Hint $invalid={!usernameValid}>영문/숫자/._- 조합, 3~20자.</Hint>
            </Field>

            {/* Display name */}
            <Field>
              <Label htmlFor="displayname">Display name</Label>
              <InputWrap data-valid={displaynameValid}>
                <LeftIcon viewBox="0 0 24 24" aria-hidden>
                  <path
                    d="M12 12a5 5 0 100-10 5 5 0 000 10z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </LeftIcon>
                <Input
                  id="displayname"
                  placeholder="예: 홍길동"
                  value={values.displayname}
                  onChange={update("displayname")}
                  aria-invalid={!displaynameValid}
                  required
                  aria-required="true"
                />
              </InputWrap>
              <Hint $invalid={!displaynameValid}>2자 이상 입력하세요.</Hint>
            </Field>

            {/* Department */}
            <Field>
              <Label htmlFor="department">Department</Label>
              <InputWrap data-valid={departmentValid}>
                <LeftIcon viewBox="0 0 24 24" aria-hidden>
                  <path
                    d="M3 4h18v6H3zM3 14h18v6H3z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </LeftIcon>
                <Input
                  id="department"
                  placeholder="예: Oasys"
                  value={values.department}
                  onChange={update("department")}
                  aria-invalid={!departmentValid}
                  required
                  aria-required="true"
                />
              </InputWrap>
              <Hint $invalid={!departmentValid}>필수 항목입니다.</Hint>
            </Field>

            {/* Email */}
            <Field>
              <LabelRow>
                <Label htmlFor="email">Email</Label>
              </LabelRow>
              <InputWrap data-valid={emailValid}>
                <LeftIcon viewBox="0 0 24 24" aria-hidden>
                  <path
                    d="M4 6h16v12H4z M4 6l8 6 8-6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </LeftIcon>
                <Input
                  id="email"
                  placeholder="name@example.com"
                  value={values.email}
                  onChange={update("email")}
                  aria-invalid={!emailValid}
                  required
                  aria-required="true"
                />
              </InputWrap>
              <Hint $invalid={!emailValid}>
                {values.email.trim().length === 0
                  ? "필수 항목입니다."
                  : "유효한 이메일 형식이 아닙니다."}
              </Hint>
            </Field>

            {/* ✅ OU name */}
            <Field>
              <Label htmlFor="ouname">OU name</Label>
              <InputWrap data-valid={ounameValid}>
                <LeftIcon viewBox="0 0 24 24" aria-hidden>
                  <path
                    d="M3 4h18v6H3zM3 14h18v6H3z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </LeftIcon>
                <Input
                  id="ouname"
                  placeholder="예: CMP-OU"
                  value={values.ouname}
                  onChange={update("ouname")}
                  aria-invalid={!ounameValid}
                  required
                  aria-required="true"
                />
              </InputWrap>
              <Hint $invalid={!ounameValid}>필수 항목입니다.</Hint>
            </Field>
          </FormGrid>

          <ModalActions>
            <ModalBtn onClick={resetForm} disabled={submitting}>
              초기화
            </ModalBtn>
            <ModalBtn onClick={onClose} disabled={submitting}>
              취소
            </ModalBtn>
            <ModalBtn $danger onClick={handleSubmit} disabled={!canSubmit}>
              {submitting ? "생성 중..." : "생성"}
            </ModalBtn>
          </ModalActions>
        </ModalCard>
      </ModalOverlay>
    </ModalPortal>
  );
};

export default AddUserModal;
